import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { produce } from 'immer';

import { gqlClient } from '@/api/graphql-client';
import proposalKeys from '@/api/queries/proposal/keyFactor';
import { graphql } from '@/gql';
import {
  GetProposalQuery,
  UpdateProposalPaymentScheduleInput,
  UpdateProposalPaymentScheduleMutation,
} from '@/gql/graphql';

const UPDATE_PROPOSAL_PAYMENT_SCHEDULE = graphql(`
  mutation UpdateProposalPaymentSchedule(
    $input: UpdateProposalPaymentScheduleInput!
  ) {
    updateProposalPaymentSchedule(input: $input) {
      id
      paymentSchedule {
        stages {
          ...PaymentScheduleStageFields
        }
        explainer
      }
    }
  }
`);

const useUpdateProposalPaymentSchedule = (args?: {
  proposalPublicId?: string;
  options?: UseMutationOptions<
    UpdateProposalPaymentScheduleMutation,
    unknown,
    UpdateProposalPaymentScheduleInput
  >;
}) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = args?.options ?? {};

  return useMutation({
    mutationFn: (input: UpdateProposalPaymentScheduleInput) => {
      return gqlClient.request(UPDATE_PROPOSAL_PAYMENT_SCHEDULE, {
        input,
      });
    },
    onSuccess: (data, variables, context) => {
      if (args?.proposalPublicId) {
        const queryKey = proposalKeys.proposal(args.proposalPublicId);
        // Patch the returned schedule straight into the cache so readers
        // update without waiting on the refetch. The invalidation still runs:
        // effectivePaymentSchedule is computed server-side from the schedule,
        // deposit and grants, so only the refetch can reconcile it.
        queryClient.setQueryData<GetProposalQuery>(queryKey, (old) =>
          old
            ? produce(old, (draft) => {
                draft.proposal.paymentSchedule =
                  data.updateProposalPaymentSchedule.paymentSchedule;
              })
            : old
        );
        void queryClient.invalidateQueries({ queryKey });
      }
      onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export default useUpdateProposalPaymentSchedule;
